/** @jsxImportSource @emotion/react */
import { Grid as MuiGrid, useMediaQuery, useTheme } from '@material-ui/core';
import { Buttons } from '@zip/business-components';
import * as Icons from '@zip/icons';
import { QuickAction } from 'components';
import { QuickActionSkeleton } from 'components/quick-action';
import { useMerchantData } from 'contexts';
import {
  FeatureFlag,
  MerchantClassSettingType,
  PageAction,
  PageRoute,
  PermissionEnum,
  QuickActionType,
} from 'enums';
import { css } from 'global';
import { useFeatureFlagEnabled, useFetch } from 'hooks';
import { FC, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { logError, logEvent } from 'utils';
import InviteCustomerDialogModule from '../invite-customer';
import QuickActionEditDialogModule from '../quick-action-edit-dialog';

import {
  confirmSpotlightPopup,
  hadSpotlightPopup,
} from 'modules/spotlight/SpotlightUtil';
import * as styles from './QuickActionList.styles';

const QuickActionListModule: FC = () => {
  const { push } = useHistory();
  const { get } = useFetch();
  const {
    profileData,
    currentUserId,
    checkPermission,
    checkMerchantDashboardSetting,
  } = useMerchantData();
  const { breakpoints } = useTheme();
  const isMobile = useMediaQuery(breakpoints.down('xs'));

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [quickActions, setQuickActions] = useState<QuickActionType[]>([]);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState<boolean>(false);
  const [isInviteCustomerDialogOpen, setIsInviteCustomerDialogOpen] =
    useState<boolean>(false);

  const [showNewFeatureTooltip, setShowNewFeatureTooltip] = useState(false);

  const productConfigEnabled = useFeatureFlagEnabled(
    FeatureFlag.EnableProductConfig
  );

  const showProductConfig =
    productConfigEnabled &&
    checkPermission(PermissionEnum.ProductConfigExecute);

  useEffect(() => {
    if (hadSpotlightPopup(currentUserId)) {
      return;
    }

    const shouldShowSpotlightValue = isMobile && showProductConfig;
    if (shouldShowSpotlightValue) {
      setShowNewFeatureTooltip(shouldShowSpotlightValue);
      confirmSpotlightPopup(currentUserId);
    }

    return (): void => {
      setShowNewFeatureTooltip(false);
    };
  }, [isMobile, showProductConfig]);

  useEffect(() => {
    if (profileData?.userProfile?.userProfileId) {
      const abortController = new AbortController();
      getUserPreferences(abortController.signal);

      return (): void => {
        abortController.abort();
      };
    }
  }, [profileData?.userProfile?.userProfileId]);

  function getUserPreferences(signal?: AbortSignal): void {
    setIsLoading(true);
    get('/userpreference', signal)
      .then((res) => res?.json())
      .then((response) => {
        const quickActionsToSet =
          response?.userPreferences?.quickActions.slice();

        // force CreditLimitIncreaseInvite action on all users it is enabled for
        if (
          checkMerchantDashboardSetting(
            MerchantClassSettingType.CLIInvitationEnabled
          ) &&
          checkPermission(PermissionEnum.CreateCLIInvitationExecute) &&
          !quickActionsToSet?.includes(
            QuickActionType.CreditLimitIncreaseInvite
          )
        ) {
          quickActionsToSet.push(QuickActionType.CreditLimitIncreaseInvite);
        }

        // Defaulted to show as 2nd item in quick action list if it doesn't exist in DB
        if (
          showProductConfig &&
          !quickActionsToSet.includes(QuickActionType.ProductsConfiguration)
        ) {
          quickActionsToSet.splice(1, 0, QuickActionType.ProductsConfiguration);
        }

        setQuickActions(quickActionsToSet);
      })
      .catch((err) => {
        // Don't log AbortError as it's expected when component unmounts
        if (err.name !== 'AbortError') {
          logError(PageAction.loadUserPreferences, err);
          setQuickActions([]);
        }
      })
      .finally(() => {
        // Only update loading state if request wasn't aborted
        if (!signal?.aborted) {
          setIsLoading(false);
        }
      });
  }

  const handleDialogOpen = (): void => setIsEditDialogOpen(true);

  const handleDialogClose = (): void => setIsEditDialogOpen(false);

  interface OpenEditDialogButtonProps {
    handleDialogOpen: () => void;
  }

  const OpenEditDialogButton = ({
    handleDialogOpen,
  }: OpenEditDialogButtonProps): JSX.Element => {
    const handleClick = (): void => {
      logEvent(PageAction.quickActionListEditDialogOpen);
      handleDialogOpen();
    };

    return (
      <Buttons.Text css={styles.editLink} onClick={handleClick}>
        <Icons.Pencil css={styles.editIcon} />
        <p className="body2" css={css.noMargin}>
          Customise
        </p>
      </Buttons.Text>
    );
  };

  const handleEditSuccess = (): void => getUserPreferences();

  const NEW_PRODUCT_CONFIG_TOOLTIP_JSX = (
    <div css={styles.toolTipContainer}>
      <div css={styles.toolTipContainerHeading}>
        <span css={styles.headingStyling}>New feature</span>
        <Icons.Close
          css={css.icon(true, '20px', '20px')}
          onClick={(): void => setShowNewFeatureTooltip(false)}
        />
      </div>
      <span className="body4">
        Learn more about your interest free period setup, promotions and
        optimise offers.
      </span>
    </div>
  );

  const getQuickAction = (quickAction): JSX.Element => {
    switch (quickAction) {
      case QuickActionType.CreateOrder:
        return (
          checkPermission(PermissionEnum.CreateOrderExecute) && (
            <QuickAction
              key={quickAction}
              actionType={quickAction}
              Icon={Icons.Plus}
              onClick={(): void => push(PageRoute.CreateOrder)}
              id="createOrderAction"
            />
          )
        );
      case QuickActionType.InviteCustomer:
        return (
          checkPermission(PermissionEnum.CreateInviteExecute) && (
            <QuickAction
              key={quickAction}
              actionType={quickAction}
              Icon={Icons.Paperplane}
              onClick={(): void => setIsInviteCustomerDialogOpen(true)}
              id="inviteCustomerAction"
            />
          )
        );
      case QuickActionType.SearchOrders:
        return (
          checkPermission(PermissionEnum.OrdersRead) && (
            <QuickAction
              key={quickAction}
              actionType={quickAction}
              Icon={Icons.Search}
              path={PageRoute.Orders}
              id="searchOrdersAction"
            />
          )
        );
      case QuickActionType.SearchCustomers:
        return (
          checkPermission(PermissionEnum.CustomersRead) && (
            <QuickAction
              key={quickAction}
              actionType={quickAction}
              Icon={Icons.User}
              path={PageRoute.Customers}
              id="searchCustomersAction"
            />
          )
        );
      case QuickActionType.CreditLimitIncreaseInvite:
        return (
          checkMerchantDashboardSetting(
            MerchantClassSettingType.CLIInvitationEnabled
          ) &&
          checkPermission(PermissionEnum.CreateCLIInvitationExecute) && (
            <QuickAction
              key={quickAction}
              actionType={quickAction}
              Icon={Icons.CurrencyDollarCircle}
              path={`${PageRoute.Tools}${PageRoute.CliInvite}`}
              id="limitIncreaseInviteAction"
            />
          )
        );
      case QuickActionType.DisbursementReports:
        return (
          checkPermission(PermissionEnum.DisbursementsRead) && (
            <QuickAction
              key={quickAction}
              actionType={quickAction}
              Icon={Icons.Statement}
              path={`${PageRoute.Settings}${PageRoute.Disbursement}`}
              id="disbursementReportsAction"
            />
          )
        );
      case QuickActionType.ManageUsers:
        return (
          checkPermission(PermissionEnum.UserManagementExecute) && (
            <QuickAction
              key={quickAction}
              actionType={quickAction}
              Icon={Icons.UserCirclePlus}
              path={`${PageRoute.Settings}${PageRoute.Users}`}
              id="manageUsersAction"
            />
          )
        );
      case QuickActionType.ProductsConfiguration:
        return (
          showProductConfig && (
            <QuickAction
              key={quickAction}
              actionType={quickAction}
              Icon={Icons.SlidersHorizontal}
              path={`${PageRoute.ProductConfig}`}
              tooltipProps={{
                id: 'manageProductConfiguration-tooltip',
                content: NEW_PRODUCT_CONFIG_TOOLTIP_JSX,
                placement: 'bottom-start',
                dense: true,
                variant: 'primary',
                maxWidth: 240,
                interactive: true,
                open: showNewFeatureTooltip,
              }}
              id="manageProductConfiguration"
            />
          )
        );
      default:
        return null;
    }
  };

  return (
    <>
      <div>
        <div css={styles.titleBar}>
          <p className="heading1" css={css.noMargin}>
            Quick actions
          </p>
          {!isLoading && (
            <OpenEditDialogButton handleDialogOpen={handleDialogOpen} />
          )}
        </div>
        <MuiGrid container spacing={2} css={styles.quickActionsContainer}>
          {isLoading &&
            Array.from(Array(4)).map((_, i) => <QuickActionSkeleton key={i} />)}
          {!isLoading &&
            quickActions?.map((quickAction) => getQuickAction(quickAction))}
        </MuiGrid>
      </div>

      <QuickActionEditDialogModule
        open={isEditDialogOpen}
        onClose={handleDialogClose}
        onSuccess={handleEditSuccess}
        quickActions={quickActions}
      />

      <InviteCustomerDialogModule
        open={isInviteCustomerDialogOpen}
        onClose={(): void => setIsInviteCustomerDialogOpen(false)}
      />
    </>
  );
};

export default QuickActionListModule;
